import type { ReactElement, ReactNode } from 'react';

import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Heading,
  Separator,
  Text,
  WhaleProvider,
} from '@rango-dev/whale';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Avatar } from '../../Profile/Avatar';
import { displayUserRank } from '../../Profile/ProfileSummary/ProfileSummary.helpers';

import styles from './LeaderboardCtaCard.module.css';

type LoginCtaProps = {
  variant: 'login';
  onLogin: () => void;
  loading?: boolean;
};

type RankPeekProps = {
  variant: 'rank';
  title: string;
  username: string;
  rank: number;
  score: ReactNode;
  scoreIcon: ReactNode;
  refreshLabel?: string;
  level?: number;
};

export type LeaderboardCtaCardProps = LoginCtaProps | RankPeekProps;

export function LeaderboardCtaCard(props: LeaderboardCtaCardProps): ReactElement {
  const { t } = useTranslation();

  if (props.variant === 'login') {
    return (
      <WhaleProvider appearance="light">
        <Card
          variant="surface"
          className={styles.gradientCard}
          size={{ initial: '1', sm: '2', md: '3' }}>
          <Flex
            justify="between"
            direction={{ md: 'column' }}
            align={{ initial: 'center', md: 'start' }}
            gap={{ initial: '3', md: '4' }}
            width="100%">
            <Flex direction="column" gap="1">
              <Heading
                size={{ initial: '2', sm: '5' }}
                weight="bold"
                align="left"
                className={styles.titleText}>
                {t('Track your progress now')}
              </Heading>
              <Text size={{ initial: '1', sm: '2' }} align="left" className={styles.subtitleText}>
                {t('Log in to see your score and ranking')}
              </Text>
            </Flex>
            <Button
              id="leaderboard-cta-login-btn"
              variant="solid"
              color="cyan"
              size={{ initial: '1', sm: '3' }}
              radius="full"
              loading={props.loading}
              onClick={props.onLogin}>
              {t('Log in')}
            </Button>
          </Flex>
        </Card>
      </WhaleProvider>
    );
  }

  const { username, title, rank, score, scoreIcon, refreshLabel, level } = props;

  return (
    <WhaleProvider appearance="light">
      <Card
        variant="surface"
        className={styles.gradientCard}
        size={{ initial: '1', sm: '2', md: '3' }}>
        <Flex direction="column" gap={{ initial: '2', sm: '3', md: '4' }} width="100%">
          <Flex direction="column" gap="1">
            <Heading size={{ initial: '2', sm: '5' }} weight="bold" className={styles.titleText}>
              {title}
            </Heading>
            <Text size={{ initial: '1', sm: '2' }} className={styles.subtitleText}>
              {refreshLabel ?? t('Refresh in 30 seconds')}
            </Text>
          </Flex>
          <Separator size="4" className={styles.separator} />
          <Flex justify="between" align="center" width="100%">
            <Flex align="center" gap="2">
              <Badge color="gray" variant="surface" size="1">
                {displayUserRank(rank)}
              </Badge>
              <Flex align="center" gap="2">
                <Box className="user-image-content" position="relative">
                  <Avatar username={username} size="3" />
                  {level && (
                    <Badge
                      color="teal"
                      variant="solid"
                      radius="large"
                      size="1"
                      className={styles.levelBadge}>
                      {level}
                    </Badge>
                  )}
                </Box>
                <Text size={{ initial: '1', md: '2' }} className={styles.usernameText}>
                  {username}
                </Text>
              </Flex>
            </Flex>
            <Flex align="center" gap="1">
              {scoreIcon}
              <Text size="1" weight="medium" className={styles.usernameText}>
                {score}
              </Text>
            </Flex>
          </Flex>
        </Flex>
      </Card>
    </WhaleProvider>
  );
}
